// Added by Jonghyung Lee on 2020-09-09
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root Store
import RootStore from '../../index'

// Store
import {
  reqGetHL7List,
  reqGetHL7,
  reqCreateHL7,
  reqUpdateHL7,
  reqDeleteHL7,
  reqCancelHL7Job,
  reqGetHL7JobLists,
  reqGetHL7Job,
  reqGetStorageList,
  getAllHL7Status,
  reqRestartHL7Tasks,
  getHL7RestartAbleTasks,
} from '@/service/hl7Store.service'

import { getQueryPath } from '@/helper/util'
import { reqGetStudyStatus } from '@/service/dataStorage/config.service'
import { DispDataStorageElementProps } from '@/components/mgmt/hl7/HL7Base'
import { HL7ElementProps, HL7TaskProps, HL7TaskInfoProps } from '@/helper/define/hl7Define'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class HL7Store {
  _totalCount: number = 0
  _elements: HL7ElementProps[] = []
  _cachedhl7?: HL7ElementProps
  _selectedGroupId?: string
  _dataStorageElement?: DispDataStorageElementProps
  /** hl7 task */
  _hl7TaskElements: HL7TaskProps[] = [] // Task의 내용물만을 포함
  _hl7TaskInfo?: HL7TaskInfoProps // message 상세 정보 등을 모두 포함
  _hl7TaskTotalCnt: number = 0
  /** hl7 status */
  _hl7Status: string[] = []
  _hl7CompletedStatus: string[] = []
  _hl7RetryStatus: string[] = []
  _hl7NecessaryRestartStatus: string[] = []

  root?: RootStore
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable(this)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // for HL7 JOBS
  getJobElements = async (conditions: string) => {
    const {
      data: { items, total_count },
    } = await reqGetHL7JobLists(conditions)
    this._setJobElements(items)
    this._setJobTotalCount(parseInt(total_count))
    return items
  }

  getJobElement = async (id: string) => {
    const { data } = await reqGetHL7Job(id)
    this._setJobElement(data)
    return data
  }

  getStorageName = async () => {
    const {
      data: { storage_name },
    } = await reqGetStorageList()
    return storage_name
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // for HL7 Server
  getElements = async (path: string) => {
    const {
      data: { total_count, items },
    } = await reqGetHL7List(path)
    this._setTotalCount(parseInt(total_count!))
    this.setElements(items!)
    return items!
  }

  getElement = async (id: string) => {
    const { data: item } = await reqGetHL7(id)
    return item
  }

  addElement = async (element: HL7ElementProps) => {
    const { data: resElement } = await reqCreateHL7(element)
    return resElement
  }

  removeElement = async (id: string) => {
    return await reqDeleteHL7(id)
  }

  updateElement = async (id: string, element: HL7ElementProps) => {
    const { data: updatedElement } = await reqUpdateHL7(id, element)
    return updatedElement.id
  }

  getStudyStatus = async (token: string) => {
    const {
      data: { items },
    } = await reqGetStudyStatus(token)
    return items
  }

  checkDuplicatedName = async (name: string) => {
    const filter = { $op: 'and', name }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'name', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements!.length > 0
  }

  checkDuplicatedORUName = async (oru_peer_name: string) => {
    const filter = { $op: 'and', oru_peer_name }
    const path = getQueryPath({ pageIndex: 0, orderBy: 'oru_peer_name', filter: JSON.stringify(filter) })

    const elements = await this.getElements(path)
    return elements!.length > 0
  }

  checkDuplicatedAddr = async ({ ip_address, port }: HL7ElementProps) => {
    const getQuery = getQueryPath({
      filter: JSON.stringify({ $op: 'and', ip_address, port }),
    })
    const reqList = [this.getElements(getQuery)]

    const [rtn, rtnDup] = await Promise.all([...reqList])
    if (rtn && rtn.length !== 0) return true
    if (rtnDup && rtnDup.length !== 0) return true
    return false
  }

  checkDuplicatedORUAddr = async ({ oru_peer_ip_address, oru_peer_port }: HL7ElementProps) => {
    const getQuery = getQueryPath({
      filter: JSON.stringify({ $op: 'and', oru_peer_ip_address, oru_peer_port }),
    })
    const reqList = [this.getElements(getQuery)]

    const [rtn, rtnDup] = await Promise.all([...reqList])
    if (rtn && rtn.length !== 0) return true
    if (rtnDup && rtnDup.length !== 0) return true
    return false
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // for HL7 Server
  _setTotalCount = (count: number) => {
    this._totalCount = count
  }

  setElements = (elements: HL7ElementProps[]) => {
    this._elements = elements
  }

  _setJobElements = (elements: HL7TaskProps[]) => {
    this._hl7TaskElements = elements
  }

  _setJobTotalCount = (count: number) => {
    this._hl7TaskTotalCnt = count
  }

  _setJobElement = (element: HL7TaskInfoProps) => {
    this._hl7TaskInfo = element
  }

  setHL7WithGroupName = (elements: HL7ElementProps[], groupElements: Map<string, string>) => {
    this._elements = elements.map((element) => {
      return {
        ...element,
        group_key: groupElements.get(element.fk_group_id!),
      }
    })
  }

  setSelectedStorageElement = (elements: DispDataStorageElementProps) => {
    this._dataStorageElement = elements
  }

  cacheStore = (element: HL7ElementProps) => {
    this._cachedhl7 = element
    this._selectedGroupId = this._cachedhl7.fk_group_id
  }

  clearCachedStore = () => {
    this._cachedhl7 = undefined
    this._selectedGroupId = undefined
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // for HL7 Tasks
  _setStatus(element: string[]) {
    this._hl7Status = element
  }

  _setCompletedStatus(element: string[]) {
    this._hl7CompletedStatus = element
  }

  _setRetryStatus(element: string[]) {
    this._hl7RetryStatus = element
  }

  _setNecessaryRestartStatus(element: string[]) {
    this._hl7NecessaryRestartStatus = element
  }

  cancelElement = async (id: string) => {
    await reqCancelHL7Job(id)
    const { task } = await this.getJobElement(id)
    const updatedJobList = this._hl7TaskElements.map((item) => {
      if (item.id === id) return task
      return item
    })
    this._setJobElements(updatedJobList)
  }

  initHL7Status = async () => {
    const {
      data: { items },
    } = await getAllHL7Status()
    let allStatusList: string[] = []

    items.forEach((item: any) => {
      const tmpStatus = item.status as string[]
      allStatusList = [...allStatusList, ...tmpStatus]

      switch (item.status_type) {
        case 'complete':
          this._setCompletedStatus(tmpStatus)
          break
        case 'retry':
          this._setRetryStatus(tmpStatus)
          break
        case 'restart':
          this._setNecessaryRestartStatus(tmpStatus)
          break
        default:
          Logger.errLog(`[HL7Store] Undefined status type: ${item.status_type}`)
          break
      }
    })

    // Set으로 중복 제거
    allStatusList = [...new Set(allStatusList)]
    allStatusList.sort((a: string, b: string) => (a > b ? 1 : -1))
    this._setStatus(allStatusList)
  }

  restartHL7Element = async (ids: string[]) => {
    await reqRestartHL7Tasks(ids)
  }

  getHL7RestartAbleTasks = async (conditions: any) => {
    const {
      data: { items },
    } = await getHL7RestartAbleTasks(conditions)
    return items
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // getter
  get elements() {
    return this._elements
  }

  get totalCount() {
    return this._totalCount
  }

  get jobTotalCount() {
    return this._hl7TaskTotalCnt
  }

  get selectedStorageElement() {
    return this._dataStorageElement
  }

  get jobElements() {
    return this._hl7TaskElements
  }

  get AllStatus() {
    return this._hl7Status
  }

  get completedStatus() {
    return this._hl7CompletedStatus
  }

  get retryStatus() {
    return this._hl7RetryStatus
  }

  get necessaryRestartStatus() {
    return this._hl7NecessaryRestartStatus
  }
}

